import { useState, useEffect } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, HelpCircle, FileText, CheckSquare, Copy } from 'lucide-react';
import { toast } from 'sonner';
import type { Step, QuestionTranslation } from '../types/consultation';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Textarea } from './ui/textarea';
import { getCountryKeyPoints, getAvailableCountries } from '../data/countryData';
import { countries } from '../data/countries';

interface GuidancePanelProps {
  step: Step;
  translation: QuestionTranslation;
  userCountry?: string;
}

export function GuidancePanel({ step, translation, userCountry }: GuidancePanelProps) {
  const { t } = useTranslation('guidance');
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isGuidanceOpen, setIsGuidanceOpen] = useState(true);
  const [isKeyPointsOpen, setIsKeyPointsOpen] = useState(false);
  const [isResponseOpen, setIsResponseOpen] = useState(true);
  const [responseText, setResponseText] = useState(translation?.detailedResponse || '');
  // State for country selection when userCountry is not available
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>('');

  const isOpenText = step.questionType === 'open-text';

  // Debug: Log userCountry value for q13
  useEffect(() => {
    if (step.questionId === 'q13-final-comments') {
      console.log('[GuidancePanel] Q13 Debug:', {
        userCountry,
        userCountryType: typeof userCountry,
        isEmpty: !userCountry,
        isEmptyString: userCountry === '',
        stepQuestionId: step.questionId,
        hasKeyPoints: !!translation?.keyPoints,
        keyPoints: translation?.keyPoints
      });
    }
  }, [step.questionId, userCountry, translation?.keyPoints]);

  // Update responseText when translation changes
  useEffect(() => {
    if (translation?.detailedResponse) {
      setResponseText(translation.detailedResponse);
    }
  }, [translation?.detailedResponse]);

  // If translation not loaded yet, show loading state
  if (!translation) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">{t('messages.editableNote')}</p>
        </div>
      </div>
    );
  }

  // Function to copy text to clipboard
  const handleCopyText = () => {
    navigator.clipboard.writeText(responseText).then(() => {
      toast.success(t('messages.copySuccess'));
    }).catch(() => {
      toast.error(t('messages.copyError'));
    });
  };

  // Function to format guidance text with automatic styling
  const formatGuidanceText = (text: string, bulletColor?: string) => {
    const lines = text.split('\n');
    const elements: ReactElement[] = [];
    let currentBulletGroup: string[] = [];
    let elementKey = 0;

    // Helper function to apply bold-before-colon formatting
    // Excludes content in parentheses ( ) from being bolded
    const formatBoldBeforeColon = (lineText: string): string | ReactElement => {
      // Match text before colon (non-greedy to get the first colon)
      const colonMatch = lineText.match(/^(.+?):(.*)$/);
      if (colonMatch) {
        const [, beforeColon, afterColon] = colonMatch;
        
        // Parse beforeColon to separate text outside parentheses from text inside parentheses
        const parts: (string | ReactElement)[] = [];
        let currentIndex = 0;
        let inParens = false;
        let parenStart = -1;
        let boldBuffer = '';
        
        for (let i = 0; i < beforeColon.length; i++) {
          const char = beforeColon[i];
          
          if (char === '(') {
            // Flush any bold buffer before entering parentheses
            if (boldBuffer.trim()) {
              parts.push(<strong key={`bold-${currentIndex++}`}>{boldBuffer}</strong>);
              boldBuffer = '';
            }
            inParens = true;
            parenStart = i;
          } else if (char === ')' && inParens) {
            // Extract parentheses content (without the parentheses themselves)
            const parenContent = beforeColon.substring(parenStart + 1, i);
            parts.push(`(${parenContent})`);
            inParens = false;
            parenStart = -1;
          } else if (!inParens) {
            // Accumulate text outside parentheses for bolding
            boldBuffer += char;
          }
        }
        
        // Flush any remaining bold buffer
        if (boldBuffer.trim()) {
          parts.push(<strong key={`bold-${currentIndex++}`}>{boldBuffer}</strong>);
        }
        
        // If we're still in parentheses at the end, add the remaining text as-is
        if (inParens && parenStart >= 0) {
          parts.push(beforeColon.substring(parenStart));
        }
        
        return (
          <>
            {parts}:{afterColon}
          </>
        );
      }
      return lineText;
    };

    // Process each line
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();
      
      // Skip empty lines
      if (trimmedLine === '') {
        // If we have accumulated bullets, flush them before skipping
        if (currentBulletGroup.length > 0) {
          elements.push(
            <ul 
              key={elementKey++} 
              className="list-disc list-outside ml-[1em] space-y-1 my-2"
              style={bulletColor ? { color: bulletColor } : undefined}
            >
              {currentBulletGroup.map((bulletText, idx) => (
                <li key={idx} className="leading-[1.5] text-foreground">
                  {formatBoldBeforeColon(bulletText)}
                </li>
              ))}
            </ul>
          );
          currentBulletGroup = [];
        }
        continue;
      }

      // Check if line starts with bullet character
      if (trimmedLine.startsWith('•')) {
        // Remove bullet character and trim
        const bulletText = trimmedLine.substring(1).trim();
        currentBulletGroup.push(bulletText);
      } else {
        // Flush any accumulated bullets first
        if (currentBulletGroup.length > 0) {
          elements.push(
            <ul 
              key={elementKey++} 
              className="list-disc list-outside ml-[1em] space-y-1 my-2"
              style={bulletColor ? { color: bulletColor } : undefined}
            >
              {currentBulletGroup.map((bulletText, idx) => (
                <li key={idx} className="leading-[1.5] text-foreground">
                  {formatBoldBeforeColon(bulletText)}
                </li>
              ))}
            </ul>
          );
          currentBulletGroup = [];
        }
        
        // Add paragraph for non-bullet line
        elements.push(
          <p key={elementKey++} className="leading-[1.5] my-2">
            {formatBoldBeforeColon(line)}
          </p>
        );
      }
    }

    // Flush any remaining bullets
    if (currentBulletGroup.length > 0) {
      elements.push(
        <ul 
          key={elementKey++} 
          className="list-disc list-outside ml-[1em] space-y-1 my-2"
          style={bulletColor ? { color: bulletColor } : undefined}
        >
          {currentBulletGroup.map((bulletText, idx) => (
            <li key={idx} className="leading-[1.5] text-foreground">
              {formatBoldBeforeColon(bulletText)}
            </li>
          ))}
        </ul>
      );
    }

    return elements;
  };

  return (
    <div className="h-full flex flex-col w-full">
      <div className="flex-1 overflow-y-auto px-8 lg:px-12 py-8 pb-32 space-y-6">
        {/* Question Header */}
        <div className="bg-orange-50 border border-orange-200 p-4">
          {translation.preQuestionNote && (
            <p className="text-[16px] mb-3 font-normal font-bold">
              {translation.preQuestionNote}
            </p>
          )}
          <div className="inline-block bg-[rgb(34,13,6)] text-[rgb(255,255,255)] px-3 py-1 mb-4 text-[16px] font-bold">
            {t('questionLabel', { number: step.questionNumber })}
          </div>
          <h1 className="text-[18px] font-bold font-normal">
            {translation.questionText || translation.instruction}
          </h1>
        </div>

        {/* Our Guidance - Collapsible (moved to top) */}
        {translation.guidanceText && (
          <Collapsible open={isGuidanceOpen} onOpenChange={setIsGuidanceOpen}>
            <CollapsibleTrigger className="w-full bg-green-50 border border-green-200 p-4 flex items-center justify-between hover:bg-green-100 transition-colors">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-green-600" />
                <h2 className="font-bold font-normal">{t('sections.ourGuidance.title')}</h2>
              </div>
              {isGuidanceOpen ? (
                <ChevronUp className="w-5 h-5 text-green-600" />
              ) : (
                <ChevronDown className="w-5 h-5 text-green-600" />
              )}
            </CollapsibleTrigger>
            <CollapsibleContent className="bg-green-50/50 border-x border-b border-green-200 p-4">
              <div className="leading-[1.5]">{formatGuidanceText(translation.guidanceText, 'var(--color-green-600)')}</div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Additional sections - Only shown for open-text questions */}
        {isOpenText && (
          <>
            {/* Key Points to Include - Collapsible */}
            {translation.keyPoints && translation.keyPoints.length > 0 && (
              <Collapsible open={isKeyPointsOpen} onOpenChange={setIsKeyPointsOpen}>
                <CollapsibleTrigger className="w-full bg-amber-50 border border-amber-200 p-4 flex items-center justify-between hover:bg-amber-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <CheckSquare className="w-5 h-5 text-amber-600" />
                    <h2 className="font-bold font-normal">{t('sections.keyPoints.title')}</h2>
                  </div>
                  {isKeyPointsOpen ? (
                    <ChevronUp className="w-5 h-5 text-amber-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-amber-600" />
                  )}
                </CollapsibleTrigger>
                <CollapsibleContent className="bg-amber-50/50 border-x border-b border-amber-200 p-4">
                  {(() => {
                    // Check if this is Q13 and first keyPoint has placeholder (English)
                    const isQ13 = step.questionId === 'q13-final-comments';
                    const firstKeyPoint = translation.keyPoints[0];
                    const hasPlaceholder = firstKeyPoint?.includes('<pulls in info>') || firstKeyPoint?.includes(',]<pulls in info>');
                    const useDynamicLogic = isQ13 && hasPlaceholder;
                    
                    if (useDynamicLogic) {
                      // English: Use dynamic country logic (only for first keyPoint with placeholder)
                      const staticText = firstKeyPoint.replace(/[,]?<pulls in info>/g, '').trim();
                      
                      // Determine which country data to use
                      const hasUserCountry = userCountry && userCountry.trim() !== '';
                      const activeCountryCode = hasUserCountry ? userCountry : selectedCountryCode;
                      const activeCountryHighlights = activeCountryCode 
                        ? getCountryKeyPoints(activeCountryCode)
                        : [];
                      
                      // Get available countries for dropdown
                      const availableCountries = getAvailableCountries();
                      const countryNameToCodeMap: Record<string, string> = {};
                      countries.forEach(c => {
                        countryNameToCodeMap[c.name] = c.code;
                      });
                      
                      return (
                        <>
                          <ul className="space-y-2">
                            <li className="flex items-start gap-2">
                              <div className="w-1.5 h-1.5 rounded-full bg-amber-600 flex-shrink-0 mt-2" />
                              <div className="flex-1">
                                <span>{staticText}</span>
                                
                                {/* Show dropdown if no userCountry or userCountry is empty */}
                                {!hasUserCountry && (
                                  <div className="mt-3 mb-3">
                                    <label htmlFor="country-select-q13" className="block text-sm font-medium text-gray-700 mb-2">
                                      {translation.countryKeyPointsLabel || 'Select your country to see relevant examples:'}
                                    </label>
                                    <select
                                      id="country-select-q13"
                                      value={selectedCountryCode}
                                      onChange={(e) => setSelectedCountryCode(e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                    >
                                      <option value="">-- Select a country --</option>
                                      {availableCountries.map((countryName) => {
                                        const code = countryNameToCodeMap[countryName];
                                        return code ? (
                                          <option key={code} value={code}>
                                            {countryName}
                                          </option>
                                        ) : null;
                                      })}
                                    </select>
                                  </div>
                                )}
                                
                                {/* Show country-specific highlights or default message */}
                                {activeCountryHighlights.length > 0 ? (
                                  <ul className="mt-2 ml-4 space-y-1">
                                    {activeCountryHighlights.map((highlight, hIndex) => (
                                      <li key={hIndex} className="flex items-start gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-amber-600 flex-shrink-0 mt-2" />
                                        <span>{highlight}</span>
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  activeCountryCode && (
                                    <p className="mt-2 text-gray-600 text-sm">
                                      {translation.countryKeyPointsDefault || 'No specific examples available for this country.'}
                                    </p>
                                  )
                                )}
                                
                                {/* Show default message when no country selected */}
                                {!activeCountryCode && (
                                  <p className="mt-2 text-gray-600 text-sm">
                                    {translation.countryKeyPointsDefault || 'You can highlight positive moves in your country or region on animal welfare.'}
                                  </p>
                                )}
                              </div>
                            </li>
                          </ul>
                        </>
                      );
                    } else if (isQ13) {
                      // Non-English Q13: Render static keyPoints (first without bullet, rest with bullets)
                      const firstKeyPoint = translation.keyPoints[0];
                      const remainingKeyPoints = translation.keyPoints.slice(1);
                      
                      return (
                        <>
                          {/* First keyPoint: Plain text without bullet */}
                          <p className="mb-2">
                            {firstKeyPoint}
                          </p>
                          {/* Remaining keyPoints: Bullet points */}
                          {remainingKeyPoints.length > 0 && (
                            <ul className="space-y-2">
                              {remainingKeyPoints.map((point, index) => (
                                <li key={index + 1} className="flex items-start gap-2">
                                  <div className="w-1.5 h-1.5 rounded-full bg-amber-600 flex-shrink-0 mt-2" />
                                  <span>{point}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </>
                      );
                    } else {
                      // Regular keyPoints (non-Q13): All bullets
                      return (
                        <ul className="space-y-2">
                          {translation.keyPoints.map((point, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <div className="w-1.5 h-1.5 rounded-full bg-amber-600 flex-shrink-0 mt-2" />
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    }
                  })()}
                </CollapsibleContent>
              </Collapsible>
            )}

            {/* Suggested Detailed Response - Collapsible */}
            {translation.detailedResponse && (
              <Collapsible open={isResponseOpen} onOpenChange={setIsResponseOpen}>
                <CollapsibleTrigger className="w-full bg-purple-50 border border-purple-200 p-4 flex items-center justify-between hover:bg-purple-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-purple-600 flex-shrink-0" />
                    <h2 className="font-bold font-normal">{t('sections.suggestedResponse.title')}</h2>
                  </div>
                  {isResponseOpen ? (
                    <ChevronUp className="w-5 h-5 text-purple-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-purple-600" />
                  )}
                </CollapsibleTrigger>
                <CollapsibleContent className="bg-purple-50/50 border-x border-b border-purple-200">
                  <div className="p-4 flex items-center justify-end border-b border-purple-200">
                    <button
                      onClick={handleCopyText}
                      className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 text-white hover:bg-purple-700 transition-colors text-[16px]"
                      title={t('buttons.copyText')}
                    >
                      <Copy className="w-4 h-4" />
                      {t('buttons.copyText')}
                    </button>
                  </div>
                  <div className="px-4 pb-4 space-y-4">
                    <Textarea
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                      className="min-h-[200px] bg-white resize-none"
                      placeholder={step.questionId === 'q13-final-comments' ? t('placeholders.q13') : step.questionId === 'q12b-citizen-contribution' ? t('placeholders.q12b') : t('placeholders.default')}
                    />
                    {translation.personalizationPrompt && (
                      <div className="bg-white/50 border-l-4 border-purple-400 p-3">
                        <p className="text-[16px] leading-[1.5]">
                          <strong>{t('personalization.noteLabel')}</strong> {translation.personalizationPrompt}
                        </p>
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
          </>
        )}

        {/* Help Understanding This Question - Always shown (moved to end) */}
        {translation.helpText && (
          <Collapsible open={isHelpOpen} onOpenChange={setIsHelpOpen}>
            <CollapsibleTrigger className="w-full bg-blue-50 border border-blue-200 p-4 flex items-center justify-between hover:bg-blue-100 transition-colors">
              <div className="flex items-center gap-3">
                <HelpCircle className="w-5 h-5 text-blue-600" />
                <h2 className="font-bold font-normal">{t('sections.help.title')}</h2>
              </div>
              {isHelpOpen ? (
                <ChevronUp className="w-5 h-5 text-blue-600" />
              ) : (
                <ChevronDown className="w-5 h-5 text-blue-600" />
              )}
            </CollapsibleTrigger>
            <CollapsibleContent className="bg-blue-50/50 border-x border-b border-blue-200 p-4">
              <div className="leading-[1.5]">{formatGuidanceText(translation.helpText, 'var(--color-blue-600)')}</div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>
    </div>
  );
}
